import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import CTASection from '../components/CTASection';
import useBrand from '../branding/useBrand';
import useDocumentTitle from '../hooks/useDocumentTitle';
import './Page.css';
import './HomePage.css';

function HomePage() {
  const content = useBrand().pages.home;
  useDocumentTitle(content.title);

  return (
    <PageLayout>
      <div className="home-page">
      <section className="page-section intro-section">
        <div className="row g-4 align-items-center">
          <div className="col-lg-6">
            <div className="intro-copy">
              <span className="intro-eyebrow">{content.eyebrow}</span>
              <h1 className="intro-title">{content.heroTitle}</h1>
              <p className="intro-description">
                {content.heroLead}
              </p>
            </div>
          </div>
          <div className="col-lg-6">
            <img
              src={content.heroImage}
              alt={content.heroImageAlt}
              className="intro-image"
            />
          </div>
        </div>
      </section>

      <section className="page-section">
        <header className="section-header">
          <h2 className="section-heading">{content.pillarsHeading}</h2>
          <p className="section-subhead">
            {content.pillarsSubhead}
          </p>
        </header>

        <div
          id="pillarsCarousel"
          className="carousel slide pillars-carousel"
          data-bs-ride="carousel"
          data-bs-interval="6000"
          data-bs-pause="hover"
        >
          <div className="carousel-indicators">
            {content.pillars.map((pillar, i) => (
              <button
                key={pillar.title}
                type="button"
                data-bs-target="#pillarsCarousel"
                data-bs-slide-to={i}
                className={i === 0 ? 'active' : ''}
                aria-current={i === 0 ? 'true' : undefined}
                aria-label={`${content.carousel.slideLabel} ${i + 1}: ${pillar.title}`}
              />
            ))}
            <button
              type="button"
              data-bs-target="#pillarsCarousel"
              data-bs-slide-to={content.pillars.length}
              aria-label={`${content.carousel.slideLabel} ${content.pillars.length + 1}: ${content.carousel.summarySlide}`}
            />
          </div>

          <div className="carousel-inner">
            {content.pillars.map((pillar, i) => (
              <div key={pillar.title} className={`carousel-item ${i === 0 ? 'active' : ''}`}>
                <div className="row g-0 align-items-center pillars-slide">
                  <div className="col-lg-7">
                    <img src={pillar.image} alt={pillar.alt} className="pillars-image" />
                  </div>
                  <div className="col-lg-5">
                    <div className="pillars-text">
                      <span className="pillars-step">{`0${i + 1} / 0${content.pillars.length + 1}`}</span>
                      <h3 className="pillars-title">{pillar.title}</h3>
                      <p className="pillars-description">{pillar.description}</p>
                      <Link to={pillar.link.to} className="pillars-link">
                        {pillar.link.label} &rarr;
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="carousel-item">
              <div className="pillars-slide pillars-slide-summary">
                <span className="pillars-step">{`0${content.pillars.length + 1} / 0${content.pillars.length + 1}`}</span>
                <h3 className="pillars-summary-title">{content.pillarsSummaryTitle}</h3>
                <p className="pillars-summary-description">
                  {content.pillarsSummaryDescription}
                </p>
                <Link to="/product" className="btn btn-primary pillars-summary-cta">
                  {content.pillarsSummaryCta} &rarr;
                </Link>
              </div>
            </div>
          </div>

          <button className="carousel-control-prev" type="button" data-bs-target="#pillarsCarousel" data-bs-slide="prev">
            <span className="carousel-control-prev-icon" aria-hidden="true" />
            <span className="visually-hidden">{content.carousel.previous}</span>
          </button>
          <button className="carousel-control-next" type="button" data-bs-target="#pillarsCarousel" data-bs-slide="next">
            <span className="carousel-control-next-icon" aria-hidden="true" />
            <span className="visually-hidden">{content.carousel.next}</span>
          </button>
        </div>
      </section>

      <section className="page-section">
        <header className="section-header">
          <h2 className="section-heading">{content.systemsHeading}</h2>
          <p className="section-subhead">
            {content.systemsSubhead}
          </p>
        </header>

        <div className="systems-grid">
          {content.systems.map((feature, i) => (
            <div
              key={feature.title}
              className={`row align-items-center systems-row ${i % 2 === 1 ? 'flex-md-row-reverse' : ''}`}
            >
              <div className="col-md-6">
                <div className="systems-text">
                  <h3 className="systems-title">{feature.title}</h3>
                  <p className="systems-body">{feature.body}</p>
                </div>
              </div>
              <div className="col-md-6">
                <img src={feature.image} alt={feature.alt} className="systems-image" />
              </div>
            </div>
          ))}
        </div>

        <p className="mt-4 mb-0 text-center">
          <Link to="/how-it-works">{content.systemsLink} &rarr;</Link>
        </p>
      </section>

      <section className="page-section">
        <h2>{content.standardsHeading}</h2>
        <div className="standards-grid">
          {content.standards.map((standard) => (
            <article key={standard.title} className="standard-card">
              <img src={standard.image} alt={standard.alt} className="standard-card-image" />
              <div className="standard-card-overlay">
                <h3 className="standard-card-title">{standard.title}</h3>
                <p className="standard-card-description">{standard.description}</p>
              </div>
            </article>
          ))}
        </div>
        <p className="mt-3 mb-0 text-center">
          <Link to="/technology">{content.standardsLink} &rarr;</Link>
        </p>
      </section>

      <section className="page-section">
        <header className="section-header">
          <h2 className="section-heading">{content.audiencesHeading}</h2>
          <p className="section-subhead">
            {content.audiencesSubhead}
          </p>
        </header>

        <div className="audience-grid">
          {content.audiences.map((audience) => (
            <article key={audience.title} className="audience-card">
              <span className="audience-card-label">{audience.label}</span>
              <h3 className="audience-card-title">{audience.title}</h3>
              <p className="audience-card-description">{audience.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="page-section">
        <header className="section-header">
          <h2 className="section-heading">{content.driverHeading}</h2>
          <p className="section-subhead">
            {content.driverSubhead}
          </p>
        </header>

        <div className="driver-flow">
          {content.driverSteps.map((step) => (
            <article key={step.title} className="driver-flow-step">
              <span className="driver-flow-node">{step.number}</span>
              <div className="driver-flow-card">
                <h3 className="driver-flow-title">{step.title}</h3>
                <p className="driver-flow-description">{step.description}</p>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-4 mb-0 text-center">
          <Link to="/how-it-works#driver-flow">{content.driverLink} &rarr;</Link>
        </p>
      </section>

      <CTASection />
      </div>
    </PageLayout>
  );
}

export default HomePage;
